/**
 * Call Center Dashboard – Two workstreams: Lead hunting & Listing follow-up.
 * Lead hunting: contacts uploaded by Super Admin; call to convert; mark follow-up / not interested / callback; convert when registered.
 * Listing follow-up: call property owners to verify listing; log call (type + what to do); create tasks.
 */

import { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Tooltip from "@mui/material/Tooltip";

import { useAuth } from "context/authContext";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

import { callCenterAPI } from "utils/api";

const LEAD_STATUSES = [
  "NEW",
  "FOLLOW_UP",
  "IN_PROGRESS",
  "CONTACTED",
  "QUALIFIED",
  "CALLBACK_SCHEDULED",
  "NO_ANSWER",
  "WRONG_NUMBER",
  "NOT_INTERESTED",
  "CONVERTED",
];
const LEAD_STATUS_LABELS = {
  NEW: "New",
  FOLLOW_UP: "Follow-up",
  IN_PROGRESS: "In progress",
  CONTACTED: "Contacted",
  QUALIFIED: "Qualified",
  CALLBACK_SCHEDULED: "Callback scheduled",
  NO_ANSWER: "No answer",
  WRONG_NUMBER: "Wrong number",
  NOT_INTERESTED: "Not interested",
  CONVERTED: "Converted",
};
const TASK_TYPES = [
  "CALLBACK",
  "DOC_REMINDER",
  "INSPECTION_CONFIRM",
  "CONTRACT_FOLLOWUP",
  "GENERAL_FOLLOWUP",
  "ESCALATION",
];
const CHANNELS = ["CALL", "WHATSAPP", "EMAIL"];
const CALL_TYPES = [
  { value: "LISTING_VERIFICATION", label: "Listing verification" },
  { value: "DOCUMENT_REMINDER", label: "Document reminder" },
  { value: "INSPECTION_CONFIRM", label: "Inspection confirmation" },
  { value: "CONTRACT_FOLLOWUP", label: "Contract follow-up" },
  { value: "GENERAL_INQUIRY", label: "General inquiry" },
  { value: "OTHER", label: "Other" },
];

function CallCenterDashboard() {
  const { user, userRole } = useAuth();
  const currentUserId = user?.id ?? user?._id;
  const isAdmin = userRole === "Admin" || userRole === "Super_Admin" || userRole === "Super Admin";

  const [mainTab, setMainTab] = useState(0);
  const [contactFilter, setContactFilter] = useState("all");
  const [listingSubTab, setListingSubTab] = useState(0);

  const [leads, setLeads] = useState([]);
  const [leadTotal, setLeadTotal] = useState(0);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadStatusFilter, setLeadStatusFilter] = useState("");
  const [callLogs, setCallLogs] = useState([]);
  const [callLogsLead, setCallLogsLead] = useState([]);
  const [callLogsListing, setCallLogsListing] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [tasksListing, setTasksListing] = useState([]);
  const [myTasks, setMyTasks] = useState([]);
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [stuck, setStuck] = useState([]);
  const [callHistoryFrom, setCallHistoryFrom] = useState("");
  const [callHistoryTo, setCallHistoryTo] = useState("");
  const [callHistorySearch, setCallHistorySearch] = useState("");
  const [convertOwnerSearch, setConvertOwnerSearch] = useState("");
  const [convertOwnerResults, setConvertOwnerResults] = useState([]);
  const [convertSearching, setConvertSearching] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [analyticsMe, setAnalyticsMe] = useState(null);

  const [addCallLogOpen, setAddCallLogOpen] = useState(false);
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertLead, setConvertLead] = useState(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState("");

  const [newCallLog, setNewCallLog] = useState({
    entityType: "LEAD",
    entityId: "",
    channel: "CALL",
    outcome: "",
    notes: "",
    nextFollowUpAt: "",
    new_status: "NEW",
    callType: "",
    actionRequired: "",
  });
  const [newTask, setNewTask] = useState({
    entityType: "LEAD",
    entityId: "",
    type: "CALLBACK",
    assignedTo: "",
    dueDate: "",
    priority: "MEDIUM",
    title: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const loadLeads = async (overrides = {}) => {
    setLoading(true);
    try {
      const params = { limit: 50, ...overrides };
      if (leadSearch.trim()) params.search = leadSearch.trim();
      if (leadStatusFilter) params.status = leadStatusFilter;
      const res = await callCenterAPI.getLeads(params);
      setLeads(res.leads || []);
      setLeadTotal(res.total ?? res.leads?.length ?? 0);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load leads" });
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogs = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getCallLogs({});
      setCallLogs(res.callLogs || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load call logs" });
    } finally {
      setLoading(false);
    }
  };

  const loadLeadCallHistory = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (callHistorySearch.trim()) params.search = callHistorySearch.trim();
      if (callHistoryFrom) params.from = callHistoryFrom;
      if (callHistoryTo) params.to = callHistoryTo;
      const res = await callCenterAPI.getLeadCallHistory(params);
      setCallLogsLead(res.callLogs || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load call history" });
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogsLead = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getCallLogs({ entityType: "LEAD" });
      setCallLogsLead(res.callLogs || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load call history" });
    } finally {
      setLoading(false);
    }
  };

  const loadCallLogsListing = async () => {
    setLoading(true);
    try {
      const [ownerRes, propRes] = await Promise.all([
        callCenterAPI.getCallLogs({ entityType: "OWNER" }),
        callCenterAPI.getCallLogs({ entityType: "PROPERTY" }),
      ]);
      const ownerLogs = ownerRes.callLogs || [];
      const propLogs = propRes.callLogs || [];
      setCallLogsListing(
        [...ownerLogs, ...propLogs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load call history" });
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getTasks({});
      setTasks(res.tasks || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load tasks" });
    } finally {
      setLoading(false);
    }
  };

  const loadTasksListing = async () => {
    setLoading(true);
    try {
      const [ownerRes, propRes] = await Promise.all([
        callCenterAPI.getTasks({ entityType: "OWNER" }),
        callCenterAPI.getTasks({ entityType: "PROPERTY" }),
      ]);
      const ownerTasks = ownerRes.tasks || [];
      const propTasks = propRes.tasks || [];
      setTasksListing(
        [...ownerTasks, ...propTasks].sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
      );
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load tasks" });
    } finally {
      setLoading(false);
    }
  };

  const loadMyTasks = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getTasksMe({});
      setMyTasks(res.tasks || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load my tasks" });
    } finally {
      setLoading(false);
    }
  };

  const loadOwners = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getOwners({});
      setOwners(res.owners || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load owners" });
    } finally {
      setLoading(false);
    }
  };

  const loadProperties = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getProperties({});
      setProperties(res.properties || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load properties" });
    } finally {
      setLoading(false);
    }
  };

  const loadStuck = async () => {
    setLoading(true);
    try {
      const res = await callCenterAPI.getFollowUpsStuck({});
      setStuck(res.stuck || []);
    } catch (e) {
      setMessage({ type: "error", text: e.message || "Failed to load stuck queue" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    callCenterAPI
      .getAnalyticsMe()
      .then((res) => setAnalyticsMe(res.analytics || null))
      .catch(() => setAnalyticsMe(null));
  }, [mainTab, message.text]);

  useEffect(() => {
    if (mainTab === 0) {
      const opts =
        contactFilter === "mine"
          ? { mine: true }
          : contactFilter === "unassigned"
          ? { unassigned: true }
          : {};
      loadLeads(opts);
    } else if (mainTab === 1) {
      loadLeads({ mine: true, excludeConverted: true });
    } else if (mainTab === 2) {
      loadLeadCallHistory();
    } else if (mainTab === 3) {
      loadMyTasks();
    } else if (mainTab === 4) {
      if (listingSubTab === 0) loadOwners();
      else if (listingSubTab === 1) loadProperties();
      else if (listingSubTab === 2) loadStuck();
      else if (listingSubTab === 4) loadCallLogsListing();
      else if (listingSubTab === 5) loadTasksListing();
    }
  }, [mainTab, contactFilter, listingSubTab]);

  useEffect(() => {
    if (mainTab !== 0 && mainTab !== 1) return;
    const opts =
      mainTab === 1
        ? { mine: true, excludeConverted: true }
        : contactFilter === "mine"
        ? { mine: true }
        : contactFilter === "unassigned"
        ? { unassigned: true }
        : {};
    loadLeads(opts);
  }, [leadSearch, leadStatusFilter]);

  useEffect(() => {
    if (
      addCallLogOpen &&
      (newCallLog.entityType === "OWNER" || newCallLog.entityType === "PROPERTY") &&
      !newCallLog.entityId
    ) {
      callCenterAPI
        .getOwners()
        .then((res) => setOwners(res.owners || []))
        .catch(() => setOwners([]));
      callCenterAPI
        .getProperties()
        .then((res) => setProperties(res.properties || []))
        .catch(() => setProperties([]));
    }
  }, [addCallLogOpen, newCallLog.entityType, newCallLog.entityId]);

  useEffect(() => {
    if (addTaskOpen && (newTask.entityType === "OWNER" || newTask.entityType === "PROPERTY")) {
      callCenterAPI
        .getOwners()
        .then((res) => setOwners(res.owners || []))
        .catch(() => setOwners([]));
      callCenterAPI
        .getProperties()
        .then((res) => setProperties(res.properties || []))
        .catch(() => setProperties([]));
    }
  }, [addTaskOpen, newTask.entityType]);

  const convertSearchTimeoutRef = useRef(null);
  useEffect(() => {
    if (!convertOpen) return;
    if (convertSearchTimeoutRef.current) clearTimeout(convertSearchTimeoutRef.current);
    const q = (convertOwnerSearch || "").trim();
    if (q.length < 2) {
      setConvertOwnerResults([]);
      return;
    }
    convertSearchTimeoutRef.current = setTimeout(() => {
      setConvertSearching(true);
      callCenterAPI
        .searchOwners(q)
        .then((res) => {
          setConvertOwnerResults(res.owners || []);
        })
        .catch(() => setConvertOwnerResults([]))
        .finally(() => setConvertSearching(false));
    }, 300);
    return () => {
      if (convertSearchTimeoutRef.current) clearTimeout(convertSearchTimeoutRef.current);
    };
  }, [convertOpen, convertOwnerSearch]);

  const getLeadParams = () =>
    mainTab === 1
      ? { mine: true, excludeConverted: true }
      : contactFilter === "mine"
      ? { mine: true }
      : contactFilter === "unassigned"
      ? { unassigned: true }
      : {};

  const handleClaimLead = async (id) => {
    try {
      await callCenterAPI.claimLead(id);
      setMessage({ type: "success", text: "Lead claimed" });
      loadLeads(getLeadParams());
    } catch (err) {
      const msg =
        err.status === 409
          ? "Lead already assigned to another agent."
          : err.message || "Failed to claim lead";
      setMessage({ type: "error", text: msg });
    }
  };

  const canEditLead = (lead) => {
    if (isAdmin) return true;
    if (!currentUserId) return false;
    const assignee = lead.assignedTo;
    if (assignee == null) return false;
    const assigneeId =
      typeof assignee === "object" && assignee !== null ? assignee._id ?? assignee.id : assignee;
    if (!assigneeId) return false;
    return String(assigneeId) === String(currentUserId);
  };

  const openConvertDialog = (lead) => {
    setConvertLead(lead);
    setSelectedOwnerId("");
    setConvertOwnerSearch("");
    setConvertOwnerResults([]);
    setConvertOpen(true);
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await callCenterAPI.updateLead(leadId, { status });
      setMessage({ type: "success", text: "Status updated" });
      loadLeads(getLeadParams());
    } catch (err) {
      const msg =
        err.status === 403
          ? "You don't have permission to modify this lead."
          : err.message || "Failed to update status";
      setMessage({ type: "error", text: msg });
    }
  };

  const openLogCallForLead = (lead) => {
    setNewCallLog({
      entityType: "LEAD",
      entityId: lead._id,
      channel: "CALL",
      outcome: "",
      notes: "",
      nextFollowUpAt: "",
      new_status: lead.status || "NEW",
      callType: "",
      actionRequired: "",
    });
    setAddCallLogOpen(true);
  };

  const openLogCallForOwner = (owner) => {
    setNewCallLog({
      entityType: "OWNER",
      entityId: owner._id,
      channel: "CALL",
      outcome: "",
      notes: "",
      nextFollowUpAt: "",
      callType: "",
      actionRequired: "",
    });
    setAddCallLogOpen(true);
  };

  const openLogCallForProperty = (property) => {
    setNewCallLog({
      entityType: "PROPERTY",
      entityId: property._id,
      channel: "CALL",
      outcome: "",
      notes: "",
      nextFollowUpAt: "",
      callType: "",
      actionRequired: "",
    });
    setAddCallLogOpen(true);
  };

  const handleConvertLead = async (e) => {
    e.preventDefault();
    if (!convertLead || !selectedOwnerId) {
      setMessage({ type: "error", text: "Select a property owner to link" });
      return;
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      await callCenterAPI.convertLeadToOwner(convertLead._id, { propertyOwnerId: selectedOwnerId });
      setMessage({ type: "success", text: "Lead converted and linked to property owner" });
      setConvertOpen(false);
      setConvertLead(null);
      setSelectedOwnerId("");
      setConvertOwnerSearch("");
      loadLeads(getLeadParams());
    } catch (err) {
      const msg =
        err.status === 403
          ? "You don't have permission to convert this lead."
          : err.message || "Failed to convert lead";
      setMessage({ type: "error", text: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateCallLog = async (e) => {
    e.preventDefault();
    if (!newCallLog.entityId) {
      setMessage({ type: "error", text: "Entity ID is required" });
      return;
    }
    if (newCallLog.entityType === "LEAD") {
      if (!newCallLog.channel || !newCallLog.new_status) {
        setMessage({ type: "error", text: "Channel and new status are required" });
        return;
      }
      setSubmitting(true);
      setMessage({ type: "", text: "" });
      try {
        const res = await callCenterAPI.logLeadCall(newCallLog.entityId, {
          channel: newCallLog.channel,
          outcome: newCallLog.outcome || undefined,
          notes: newCallLog.notes || undefined,
          next_follow_up_at: newCallLog.nextFollowUpAt || undefined,
          new_status: newCallLog.new_status,
        });
        setMessage({
          type: "success",
          text: res.taskCreated ? "Call logged. Follow-up task created." : "Call logged",
        });
        setAddCallLogOpen(false);
        setNewCallLog({
          entityType: "LEAD",
          entityId: "",
          channel: "CALL",
          outcome: "",
          notes: "",
          nextFollowUpAt: "",
          new_status: "NEW",
          callType: "",
          actionRequired: "",
        });
        loadLeads(getLeadParams());
        loadLeadCallHistory();
        loadMyTasks();
      } catch (err) {
        const msg =
          err.status === 403
            ? "You don't have permission to log calls for this lead."
            : err.message || "Failed to log call";
        setMessage({ type: "error", text: msg });
      } finally {
        setSubmitting(false);
      }
      return;
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      const payload = {
        entityType: newCallLog.entityType,
        entityId: newCallLog.entityId,
        channel: newCallLog.channel,
        outcome: newCallLog.outcome || undefined,
        notes: newCallLog.notes || undefined,
        nextFollowUpAt: newCallLog.nextFollowUpAt || undefined,
      };
      if (newCallLog.entityType === "OWNER" || newCallLog.entityType === "PROPERTY") {
        if (newCallLog.callType) payload.callType = newCallLog.callType;
        if (newCallLog.actionRequired) payload.actionRequired = newCallLog.actionRequired;
      }
      await callCenterAPI.createCallLog(payload);
      setMessage({ type: "success", text: "Call log created" });
      setAddCallLogOpen(false);
      setNewCallLog({
        entityType: "LEAD",
        entityId: "",
        channel: "CALL",
        outcome: "",
        notes: "",
        nextFollowUpAt: "",
        new_status: "NEW",
        callType: "",
        actionRequired: "",
      });
      loadCallLogsListing();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to create call log" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMarkTaskDone = async (taskId) => {
    try {
      await callCenterAPI.updateTask(taskId, { status: "DONE" });
      setMessage({ type: "success", text: "Task marked done" });
      loadMyTasks();
    } catch (err) {
      const msg =
        err.status === 403
          ? "You don't have permission to update this task."
          : err.message || "Failed to update task";
      setMessage({ type: "error", text: msg });
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!newTask.entityId || !newTask.assignedTo || !newTask.dueDate) {
      setMessage({ type: "error", text: "Entity ID, assigned user, and due date are required" });
      return;
    }
    setSubmitting(true);
    setMessage({ type: "", text: "" });
    try {
      await callCenterAPI.createTask({
        entityType: newTask.entityType,
        entityId: newTask.entityId,
        type: newTask.type,
        assignedTo: newTask.assignedTo,
        dueDate: newTask.dueDate,
        priority: newTask.priority,
        title: newTask.title || undefined,
        description: newTask.description || undefined,
      });
      setMessage({ type: "success", text: "Task created" });
      setAddTaskOpen(false);
      setNewTask({
        entityType: "LEAD",
        entityId: "",
        type: "CALLBACK",
        assignedTo: "",
        dueDate: "",
        priority: "MEDIUM",
        title: "",
        description: "",
      });
      loadTasks();
      if (newTask.entityType === "OWNER" || newTask.entityType === "PROPERTY") loadTasksListing();
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to create task" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={2}>
          <MDTypography variant="h4" fontWeight="medium">
            Call Center
          </MDTypography>
          <MDTypography variant="body2" color="text.secondary">
            Lead hunting: convert contacts to property owners. Listing follow-up: verify listings,
            log calls (type + what to do), create tasks.
          </MDTypography>
        </MDBox>

        {message.text && (
          <Alert
            severity={message.type}
            onClose={() => setMessage({ type: "", text: "" })}
            sx={{ mb: 2 }}
          >
            {message.text}
          </Alert>
        )}

        {analyticsMe && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                title="Calls today"
                count={analyticsMe.callsToday ?? 0}
                icon="phone"
                color="info"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                title="Callbacks due today"
                count={analyticsMe.callbacksDueToday ?? 0}
                icon="schedule"
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                title="Overdue tasks"
                count={analyticsMe.overdueCount ?? 0}
                icon="warning"
                color="error"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ComplexStatisticsCard
                title="Conversions this month"
                count={analyticsMe.conversionsThisMonth ?? 0}
                icon="person_add"
                color="success"
              />
            </Grid>
          </Grid>
        )}

        <Card>
          <Tabs
            value={mainTab}
            onChange={(_, v) => setMainTab(v)}
            sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}
          >
            <Tab label="Contacts" />
            <Tab label="Claimed Leads" />
            <Tab label="Call History" />
            <Tab label="My Tasks" />
            <Tab label="Listing follow-up" />
          </Tabs>

          <MDBox p={2}>
            {mainTab === 0 && (
              <>
                <MDBox display="flex" flexWrap="wrap" gap={2} mb={2} alignItems="center">
                  <Button
                    size="small"
                    variant={contactFilter === "all" ? "contained" : "outlined"}
                    onClick={() => setContactFilter("all")}
                  >
                    All
                  </Button>
                  <Button
                    size="small"
                    variant={contactFilter === "unassigned" ? "contained" : "outlined"}
                    onClick={() => setContactFilter("unassigned")}
                  >
                    Unassigned
                  </Button>
                  <Button
                    size="small"
                    variant={contactFilter === "mine" ? "contained" : "outlined"}
                    onClick={() => setContactFilter("mine")}
                  >
                    Claimed by me
                  </Button>
                  <TextField
                    size="small"
                    placeholder="Search name, email, phone"
                    value={leadSearch}
                    onChange={(e) => setLeadSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                  />
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={leadStatusFilter}
                      label="Status"
                      onChange={(e) => setLeadStatusFilter(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      {LEAD_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {LEAD_STATUS_LABELS[s] || s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <MDTypography variant="caption" color="text.secondary">
                    Log every call you make. Claim, update status, convert when they register.
                    Schedule a callback only when you agreed to call again.
                  </MDTypography>
                </MDBox>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Assignee</TableCell>
                          <TableCell>Last Contacted</TableCell>
                          <TableCell>Next Follow-up</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center">
                              No contacts
                            </TableCell>
                          </TableRow>
                        ) : (
                          leads.map((lead) => {
                            const canEdit = canEditLead(lead);
                            return (
                              <TableRow key={lead._id}>
                                <TableCell>{lead.name}</TableCell>
                                <TableCell>{lead.phone}</TableCell>
                                <TableCell>{lead.email}</TableCell>
                                <TableCell>
                                  <FormControl size="small" sx={{ minWidth: 140 }}>
                                    <Select
                                      value={lead.status}
                                      onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                                      disabled={!canEdit}
                                      sx={{ height: 32, fontSize: "0.875rem" }}
                                    >
                                      {LEAD_STATUSES.map((s) => (
                                        <MenuItem key={s} value={s}>
                                          {LEAD_STATUS_LABELS[s] || s}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>
                                </TableCell>
                                <TableCell>{lead.assignedTo?.fullName || "—"}</TableCell>
                                <TableCell>
                                  {lead.lastContactedAt
                                    ? new Date(lead.lastContactedAt).toLocaleString()
                                    : "—"}
                                </TableCell>
                                <TableCell>
                                  {lead.nextFollowUpAt
                                    ? new Date(lead.nextFollowUpAt).toLocaleString()
                                    : "—"}
                                </TableCell>
                                <TableCell align="right">
                                  {!lead.assignedTo && (
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={() => handleClaimLead(lead._id)}
                                      sx={{ mr: 0.5 }}
                                    >
                                      Claim
                                    </Button>
                                  )}
                                  {canEdit ? (
                                    <>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => openLogCallForLead(lead)}
                                        sx={{ mr: 0.5 }}
                                      >
                                        Log call
                                      </Button>
                                      {lead.status !== "CONVERTED" && (
                                        <Button
                                          size="small"
                                          color="success"
                                          variant="outlined"
                                          onClick={() => openConvertDialog(lead)}
                                        >
                                          Convert to owner
                                        </Button>
                                      )}
                                    </>
                                  ) : (
                                    <Tooltip title="Assigned to another agent">
                                      <span>
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled
                                          sx={{ mr: 0.5 }}
                                        >
                                          Log call
                                        </Button>
                                        {lead.status !== "CONVERTED" && (
                                          <Button
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                            disabled
                                          >
                                            Convert to owner
                                          </Button>
                                        )}
                                      </span>
                                    </Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <MDTypography variant="caption" color="text.secondary">
                  Total: {leadTotal}
                </MDTypography>
              </>
            )}

            {mainTab === 1 && (
              <>
                <MDTypography variant="body2" color="text.secondary" mb={2}>
                  Leads assigned to you (excluding converted). Log every call you make; set
                  &quot;Schedule callback&quot; only when you agreed to call again.
                </MDTypography>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Name</TableCell>
                          <TableCell>Phone</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Last Contacted</TableCell>
                          <TableCell>Next Follow-up</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {leads.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No claimed leads
                            </TableCell>
                          </TableRow>
                        ) : (
                          leads.map((lead) => (
                            <TableRow key={lead._id}>
                              <TableCell>{lead.name}</TableCell>
                              <TableCell>{lead.phone}</TableCell>
                              <TableCell>{lead.email}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={LEAD_STATUS_LABELS[lead.status] || lead.status}
                                />
                              </TableCell>
                              <TableCell>
                                {lead.lastContactedAt
                                  ? new Date(lead.lastContactedAt).toLocaleString()
                                  : "—"}
                              </TableCell>
                              <TableCell>
                                {lead.nextFollowUpAt
                                  ? new Date(lead.nextFollowUpAt).toLocaleString()
                                  : "—"}
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openLogCallForLead(lead)}
                                  sx={{ mr: 0.5 }}
                                >
                                  Log call
                                </Button>
                                {lead.status !== "CONVERTED" && (
                                  <Button
                                    size="small"
                                    color="success"
                                    variant="outlined"
                                    onClick={() => openConvertDialog(lead)}
                                  >
                                    Convert to owner
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {mainTab === 2 && (
              <>
                <MDBox display="flex" flexWrap="wrap" gap={2} mb={2}>
                  <TextField
                    size="small"
                    placeholder="Search lead name, email, phone"
                    value={callHistorySearch}
                    onChange={(e) => setCallHistorySearch(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    InputLabelProps={{ shrink: true }}
                    value={callHistoryFrom}
                    onChange={(e) => setCallHistoryFrom(e.target.value)}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    InputLabelProps={{ shrink: true }}
                    value={callHistoryTo}
                    onChange={(e) => setCallHistoryTo(e.target.value)}
                  />
                  <Button size="small" variant="outlined" onClick={() => loadLeadCallHistory()}>
                    Apply
                  </Button>
                </MDBox>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Lead</TableCell>
                          <TableCell>Agent</TableCell>
                          <TableCell>Channel</TableCell>
                          <TableCell>Outcome</TableCell>
                          <TableCell>Next follow-up</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {callLogsLead.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No call history
                            </TableCell>
                          </TableRow>
                        ) : (
                          callLogsLead.map((log) => (
                            <TableRow key={log._id}>
                              <TableCell>
                                {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                              </TableCell>
                              <TableCell>
                                {log.lead
                                  ? `${log.lead.name || ""} ${log.lead.phone || ""}`.trim() || "—"
                                  : log.entityId?.toString().slice(-6) || "—"}
                              </TableCell>
                              <TableCell>{log.agent?.fullName || "—"}</TableCell>
                              <TableCell>{log.channel}</TableCell>
                              <TableCell>{log.outcome || "—"}</TableCell>
                              <TableCell>
                                {log.nextFollowUpAt
                                  ? new Date(log.nextFollowUpAt).toLocaleString()
                                  : "—"}
                              </TableCell>
                              <TableCell>{log.notes || "—"}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {mainTab === 3 && (
              <>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                  </MDBox>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Due</TableCell>
                          <TableCell>Title</TableCell>
                          <TableCell>Lead</TableCell>
                          <TableCell>Priority</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {myTasks.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              No tasks due today or overdue
                            </TableCell>
                          </TableRow>
                        ) : (
                          myTasks.map((t) => (
                            <TableRow key={t._id}>
                              <TableCell>
                                {t.dueDate ? new Date(t.dueDate).toLocaleString() : "—"}
                              </TableCell>
                              <TableCell>{t.title || t.type}</TableCell>
                              <TableCell>
                                {t.entityType === "LEAD"
                                  ? t.entityId?.toString().slice(-6) || "—"
                                  : `${t.entityType} ${t.entityId?.toString().slice(-6)}`}
                              </TableCell>
                              <TableCell>{t.priority || "—"}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={t.derivedStatus || t.status}
                                  color={
                                    t.derivedStatus === "OVERDUE"
                                      ? "error"
                                      : t.status === "DONE"
                                      ? "success"
                                      : "default"
                                  }
                                />
                              </TableCell>
                              <TableCell align="right">
                                {t.status === "OPEN" && (
                                  <Button
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    onClick={() => handleMarkTaskDone(t._id)}
                                  >
                                    Mark done
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {mainTab === 4 && (
              <>
                <Tabs
                  value={listingSubTab}
                  onChange={(_, v) => setListingSubTab(v)}
                  sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
                >
                  <Tab label="Owners" />
                  <Tab label="Properties" />
                  <Tab label="Stuck queue" />
                  <Tab label="Log a call" />
                  <Tab label="Call history" />
                  <Tab label="Tasks" />
                </Tabs>

                {listingSubTab === 0 && (
                  <>
                    <MDTypography variant="body2" color="text.secondary" mb={2}>
                      Call property owners to verify listing. Log call with type of call and what to
                      do.
                    </MDTypography>
                    {loading ? (
                      <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </MDBox>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {owners.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No owners
                                </TableCell>
                              </TableRow>
                            ) : (
                              owners.map((o) => (
                                <TableRow key={o._id}>
                                  <TableCell>{o.fullName}</TableCell>
                                  <TableCell>{o.email}</TableCell>
                                  <TableCell>{o.phone || "—"}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => openLogCallForOwner(o)}
                                    >
                                      Log call
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                {listingSubTab === 1 && (
                  <>
                    {loading ? (
                      <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </MDBox>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Property</TableCell>
                              <TableCell>Status / Phase</TableCell>
                              <TableCell>Owner</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {properties.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  No properties
                                </TableCell>
                              </TableRow>
                            ) : (
                              properties.map((p) => (
                                <TableRow key={p._id}>
                                  <TableCell>{p.title || p._id?.slice(-6)}</TableCell>
                                  <TableCell>
                                    {p.status || "—"} / {p.phase || "—"}
                                  </TableCell>
                                  <TableCell>{p.owner?.fullName || "—"}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => openLogCallForProperty(p)}
                                    >
                                      Log call
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                {listingSubTab === 2 && (
                  <>
                    <MDTypography variant="body2" color="text.secondary" mb={2}>
                      Properties stuck in a phase (no update for configured days). Follow up to
                      verify listing.
                    </MDTypography>
                    {loading ? (
                      <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </MDBox>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Property</TableCell>
                              <TableCell>Status / Phase</TableCell>
                              <TableCell>Last updated</TableCell>
                              <TableCell>Owner</TableCell>
                              <TableCell align="right">Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stuck.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  No stuck properties
                                </TableCell>
                              </TableRow>
                            ) : (
                              stuck.map((s) => (
                                <TableRow key={s._id}>
                                  <TableCell>{s.title || s._id?.slice(-6)}</TableCell>
                                  <TableCell>
                                    {s.status || "—"} / {s.phase || "—"}
                                  </TableCell>
                                  <TableCell>
                                    {s.updatedAt ? new Date(s.updatedAt).toLocaleString() : "—"}
                                  </TableCell>
                                  <TableCell>{s.owner?.fullName || "—"}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => openLogCallForProperty(s)}
                                    >
                                      Log call
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                {listingSubTab === 3 && (
                  <MDBox maxWidth={480}>
                    <MDTypography variant="body2" color="text.secondary" mb={2}>
                      Log every call you make. Choose owner or property, then log with type and what
                      to do. Schedule a callback only if you agreed to call again.
                    </MDTypography>
                    <MDButton
                      variant="gradient"
                      color="primary"
                      size="small"
                      onClick={() => {
                        setNewCallLog({
                          entityType: "OWNER",
                          entityId: "",
                          channel: "CALL",
                          outcome: "",
                          notes: "",
                          nextFollowUpAt: "",
                          callType: "",
                          actionRequired: "",
                        });
                        setAddCallLogOpen(true);
                      }}
                      startIcon={<Icon>add</Icon>}
                      sx={{ mr: 1 }}
                    >
                      Log a call
                    </MDButton>
                    <MDButton
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => setAddTaskOpen(true)}
                      startIcon={<Icon>add</Icon>}
                    >
                      Create task
                    </MDButton>
                  </MDBox>
                )}

                {listingSubTab === 4 && (
                  <>
                    {loading ? (
                      <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </MDBox>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Entity</TableCell>
                              <TableCell>Type of call</TableCell>
                              <TableCell>What to do</TableCell>
                              <TableCell>Channel</TableCell>
                              <TableCell>Outcome</TableCell>
                              <TableCell>Agent</TableCell>
                              <TableCell>Date</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {callLogsListing.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center">
                                  No call history
                                </TableCell>
                              </TableRow>
                            ) : (
                              callLogsListing.map((log) => (
                                <TableRow key={log._id}>
                                  <TableCell>
                                    {log.entityType} {log.entityId?.toString().slice(-6)}
                                  </TableCell>
                                  <TableCell>
                                    {log.callType
                                      ? CALL_TYPES.find((c) => c.value === log.callType)?.label ||
                                        log.callType
                                      : "—"}
                                  </TableCell>
                                  <TableCell>{log.actionRequired || "—"}</TableCell>
                                  <TableCell>{log.channel}</TableCell>
                                  <TableCell>{log.outcome || "—"}</TableCell>
                                  <TableCell>{log.agent?.fullName || "—"}</TableCell>
                                  <TableCell>
                                    {log.createdAt ? new Date(log.createdAt).toLocaleString() : "—"}
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}

                {listingSubTab === 5 && (
                  <>
                    {loading ? (
                      <MDBox display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                      </MDBox>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Type</TableCell>
                              <TableCell>Entity</TableCell>
                              <TableCell>Assigned</TableCell>
                              <TableCell>Due</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {tasksListing.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={5} align="center">
                                  No tasks for owners/properties
                                </TableCell>
                              </TableRow>
                            ) : (
                              tasksListing.map((t) => (
                                <TableRow key={t._id}>
                                  <TableCell>{t.type}</TableCell>
                                  <TableCell>
                                    {t.entityType} {t.entityId?.toString().slice(-6)}
                                  </TableCell>
                                  <TableCell>{t.assignedTo?.fullName || "—"}</TableCell>
                                  <TableCell>
                                    {t.dueDate ? new Date(t.dueDate).toLocaleDateString() : "—"}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      size="small"
                                      label={t.status}
                                      color={
                                        t.status === "DONE"
                                          ? "success"
                                          : t.status === "OVERDUE"
                                          ? "error"
                                          : "default"
                                      }
                                    />
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                )}
              </>
            )}
          </MDBox>
        </Card>
      </MDBox>

      {/* Convert Lead to Owner Dialog */}
      <Dialog
        open={convertOpen}
        onClose={() => {
          setConvertOpen(false);
          setConvertLead(null);
          setSelectedOwnerId("");
          setConvertOwnerSearch("");
          setConvertOwnerResults([]);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Convert lead to property owner</DialogTitle>
        <form onSubmit={handleConvertLead}>
          <DialogContent>
            {convertLead && (
              <MDTypography variant="body2" color="text.secondary" mb={2}>
                Link lead <strong>{convertLead.name}</strong> ({convertLead.email}) to an existing
                property owner. Search by name, email, or phone.
              </MDTypography>
            )}
            <TextField
              fullWidth
              size="small"
              label="Search property owner"
              value={convertOwnerSearch}
              onChange={(e) => {
                setConvertOwnerSearch(e.target.value);
                setSelectedOwnerId("");
              }}
              placeholder="Type name, email, or phone (min 2 chars)"
              disabled={submitting}
              sx={{ mb: 2 }}
            />
            {convertSearching && (
              <MDTypography variant="caption" color="text.secondary">
                Searching…
              </MDTypography>
            )}
            {convertOwnerResults.length > 0 && !selectedOwnerId && (
              <MDBox
                sx={{
                  maxHeight: 240,
                  overflow: "auto",
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                {convertOwnerResults.map((o) => (
                  <MenuItem
                    key={o._id}
                    onClick={() => {
                      setSelectedOwnerId(o._id);
                      setConvertOwnerSearch(`${o.fullName} — ${o.email}`);
                      setConvertOwnerResults([]);
                    }}
                  >
                    {o.fullName} — {o.email} {o.phone ? `(${o.phone})` : ""}
                  </MenuItem>
                ))}
              </MDBox>
            )}
            {selectedOwnerId && (
              <MDTypography variant="body2" color="primary" sx={{ mt: 1 }}>
                Selected owner. Click Convert to confirm.
              </MDTypography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setConvertOpen(false);
                setConvertLead(null);
                setSelectedOwnerId("");
                setConvertOwnerSearch("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="success"
              disabled={!selectedOwnerId || submitting}
            >
              {submitting ? "Converting…" : "Convert"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add Call Log Dialog - Log every call; next follow-up only when scheduling a callback */}
      <Dialog
        open={addCallLogOpen}
        onClose={() => setAddCallLogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Log this call</DialogTitle>
        <form onSubmit={handleCreateCallLog}>
          <DialogContent>
            <MDTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Log every call you make. Only set &quot;Schedule callback&quot; below if you agreed to
              call again.
            </MDTypography>
            <Grid container spacing={2}>
              {newCallLog.entityType !== "LEAD" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Entity type</InputLabel>
                      <Select
                        value={newCallLog.entityType}
                        label="Entity type"
                        onChange={(e) =>
                          setNewCallLog((p) => ({ ...p, entityType: e.target.value, entityId: "" }))
                        }
                      >
                        <MenuItem value="LEAD">Lead</MenuItem>
                        <MenuItem value="OWNER">Owner</MenuItem>
                        <MenuItem value="PROPERTY">Property</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {newCallLog.entityType === "OWNER" ? (
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Owner</InputLabel>
                        <Select
                          value={newCallLog.entityId}
                          label="Owner"
                          onChange={(e) =>
                            setNewCallLog((p) => ({ ...p, entityId: e.target.value }))
                          }
                        >
                          <MenuItem value="">Select owner</MenuItem>
                          {owners.map((o) => (
                            <MenuItem key={o._id} value={o._id}>
                              {o.fullName} — {o.email}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : newCallLog.entityType === "PROPERTY" ? (
                      <FormControl fullWidth size="small" required>
                        <InputLabel>Property</InputLabel>
                        <Select
                          value={newCallLog.entityId}
                          label="Property"
                          onChange={(e) =>
                            setNewCallLog((p) => ({ ...p, entityId: e.target.value }))
                          }
                        >
                          <MenuItem value="">Select property</MenuItem>
                          {properties.map((p) => (
                            <MenuItem key={p._id} value={p._id}>
                              {p.title || p._id?.slice(-6)}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        label="Entity ID"
                        value={newCallLog.entityId}
                        onChange={(e) => setNewCallLog((p) => ({ ...p, entityId: e.target.value }))}
                        required
                      />
                    )}
                  </Grid>
                </>
              )}
              {newCallLog.entityType === "LEAD" && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>New status</InputLabel>
                    <Select
                      value={newCallLog.new_status || "NEW"}
                      label="New status"
                      onChange={(e) => setNewCallLog((p) => ({ ...p, new_status: e.target.value }))}
                    >
                      {LEAD_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                          {LEAD_STATUS_LABELS[s] || s}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {(newCallLog.entityType === "OWNER" || newCallLog.entityType === "PROPERTY") && (
                <>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type of call</InputLabel>
                      <Select
                        value={newCallLog.callType || ""}
                        label="Type of call"
                        onChange={(e) => setNewCallLog((p) => ({ ...p, callType: e.target.value }))}
                      >
                        <MenuItem value="">—</MenuItem>
                        {CALL_TYPES.map((c) => (
                          <MenuItem key={c.value} value={c.value}>
                            {c.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="What to do"
                      value={newCallLog.actionRequired || ""}
                      onChange={(e) =>
                        setNewCallLog((p) => ({ ...p, actionRequired: e.target.value }))
                      }
                      placeholder="Action required"
                    />
                  </Grid>
                </>
              )}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Channel</InputLabel>
                  <Select
                    value={newCallLog.channel}
                    label="Channel"
                    onChange={(e) => setNewCallLog((p) => ({ ...p, channel: e.target.value }))}
                  >
                    {CHANNELS.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Outcome"
                  value={newCallLog.outcome}
                  onChange={(e) => setNewCallLog((p) => ({ ...p, outcome: e.target.value }))}
                  required={newCallLog.entityType === "LEAD"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Notes"
                  multiline
                  rows={2}
                  value={newCallLog.notes}
                  onChange={(e) => setNewCallLog((p) => ({ ...p, notes: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Schedule callback (optional – only if you agreed to call again)"
                  InputLabelProps={{ shrink: true }}
                  value={newCallLog.nextFollowUpAt}
                  onChange={(e) => setNewCallLog((p) => ({ ...p, nextFollowUpAt: e.target.value }))}
                  placeholder="Leave empty if not scheduling a callback"
                />
                {newCallLog.entityType === "LEAD" && newCallLog.nextFollowUpAt && (
                  <MDTypography
                    variant="caption"
                    color="primary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    A follow-up task will be created for this date.
                  </MDTypography>
                )}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddCallLogOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={addTaskOpen} onClose={() => setAddTaskOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create task</DialogTitle>
        <form onSubmit={handleCreateTask}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Entity type</InputLabel>
                  <Select
                    value={newTask.entityType}
                    label="Entity type"
                    onChange={(e) =>
                      setNewTask((p) => ({ ...p, entityType: e.target.value, entityId: "" }))
                    }
                  >
                    <MenuItem value="LEAD">Lead</MenuItem>
                    <MenuItem value="OWNER">Owner</MenuItem>
                    <MenuItem value="PROPERTY">Property</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                {newTask.entityType === "OWNER" ? (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Owner</InputLabel>
                    <Select
                      value={newTask.entityId}
                      label="Owner"
                      onChange={(e) => setNewTask((p) => ({ ...p, entityId: e.target.value }))}
                    >
                      <MenuItem value="">Select owner</MenuItem>
                      {owners.map((o) => (
                        <MenuItem key={o._id} value={o._id}>
                          {o.fullName} — {o.email}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : newTask.entityType === "PROPERTY" ? (
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Property</InputLabel>
                    <Select
                      value={newTask.entityId}
                      label="Property"
                      onChange={(e) => setNewTask((p) => ({ ...p, entityId: e.target.value }))}
                    >
                      <MenuItem value="">Select property</MenuItem>
                      {properties.map((p) => (
                        <MenuItem key={p._id} value={p._id}>
                          {p.title || p._id?.slice(-6)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <TextField
                    fullWidth
                    size="small"
                    label="Entity ID"
                    value={newTask.entityId}
                    onChange={(e) => setNewTask((p) => ({ ...p, entityId: e.target.value }))}
                    required
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={newTask.type}
                    label="Type"
                    onChange={(e) => setNewTask((p) => ({ ...p, type: e.target.value }))}
                  >
                    {TASK_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Assigned user ID"
                  value={newTask.assignedTo}
                  onChange={(e) => setNewTask((p) => ({ ...p, assignedTo: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Due date"
                  InputLabelProps={{ shrink: true }}
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask((p) => ({ ...p, dueDate: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Priority</InputLabel>
                  <Select
                    value={newTask.priority}
                    label="Priority"
                    onChange={(e) => setNewTask((p) => ({ ...p, priority: e.target.value }))}
                  >
                    <MenuItem value="LOW">LOW</MenuItem>
                    <MenuItem value="MEDIUM">MEDIUM</MenuItem>
                    <MenuItem value="HIGH">HIGH</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Title"
                  value={newTask.title}
                  onChange={(e) => setNewTask((p) => ({ ...p, title: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  multiline
                  rows={2}
                  value={newTask.description}
                  onChange={(e) => setNewTask((p) => ({ ...p, description: e.target.value }))}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddTaskOpen(false)}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={submitting}>
              {submitting ? "Creating…" : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default CallCenterDashboard;
